import React from 'react'

import { useHotkeys } from 'react-hotkeys-hook'

import { ButtonLink } from '~/components/component-library/Button/components/ButtonLink'
import { ButtonShortcut } from '~/components/component-library/Button/components/ButtonShortcut'
import { buttonVariants } from '~/components/component-library/Button/styles'
import { type ButtonProps } from '~/components/component-library/Button/types'
import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'
import { cn } from '~/utils/tailwind-utils'
import { sendTrackingData } from '~/utils/tracking'

export const ButtonInner = ({
  ref,
  variant = 'primary',
  size = 'base',
  eventName,
  eventProperties,
  isDisabled,
  keepContainerOpaqueOnDisabled = false,
  isLoading,
  fullWidth,
  prefix,
  suffix,
  alignLeft,
  onClick,
  href,
  children,
  shortcut,
  type,
  className,
  tabIndex,
  disabledReason,
  isSelected,
  shape,
  ...restProps
}: ButtonProps & {
  ref?: React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>
}) => {
  const trackEvent = React.useCallback(() => {
    if (eventName) sendTrackingData(eventName, eventProperties)
  }, [eventName, eventProperties])

  useHotkeys(
    shortcut?.key ?? '',
    (e: KeyboardEvent) => {
      trackEvent()
      shortcut?.callback?.(e)
    },
    {
      enabled: !!shortcut && !!shortcut?.callback && !isDisabled && !isLoading,
      description: shortcut?.description,
      enableOnFormTags:
        shortcut?.enableOnFormTags || shortcut?.key === 'meta+enter'
          ? ['INPUT', 'TEXTAREA', 'SELECT']
          : undefined,
    },
  )

  const isInert = !!(isDisabled || isLoading)

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      // if disabled or loading, stop the event and do nothing
      if (isInert) {
        e.stopPropagation()
        e.preventDefault()
        return
      }
      // if there is an event name, send the tracking data
      trackEvent()
      if (onClick) onClick(e as React.MouseEvent<HTMLButtonElement>)
    },
    [trackEvent, onClick, isInert],
  )

  const isIconOnly = !!(prefix || suffix) && !children

  const buttonClassName = cn(
    buttonVariants({
      variant,
      shape,
      isSelected,
      isDisabled: isInert,
      isLoading,
      fullWidth,
      alignLeft,
      size,
    }),
    isIconOnly &&
      (size === 'small'
        ? 'px-2 py-2 [&>svg]:my-0'
        : 'px-3.5 py-3.5 [&>svg]:my-0'),
    isInert &&
      keepContainerOpaqueOnDisabled &&
      'opacity-100 disabled:opacity-100 disabled:[&_*]:opacity-50',
    isDisabled && disabledReason && 'pointer-events-none',
    className,
  )

  // secondaryAction is text-sized; the spinner takes the button's text color
  const spinnerSize = variant === 'secondaryAction' ? 'sm' : 'xl'
  const loadingElement = isLoading ? (
    <LoadingSpinner size={spinnerSize} />
  ) : null

  const buttonContent = (
    <>
      {prefix && isLoading ? loadingElement : prefix}
      {!prefix && !suffix && isLoading && loadingElement}
      {children}
      {suffix && isLoading ? loadingElement : suffix}
      {shortcut && (
        <ButtonShortcut
          shortcut={shortcut}
          variant={variant}
          fullWidth={fullWidth}
          size={size}
        />
      )}
    </>
  )

  if (href?.path) {
    return (
      <ButtonLink
        {...restProps}
        href={href}
        className={buttonClassName}
        ref={ref as React.RefObject<HTMLAnchorElement | null>}
        tabIndex={tabIndex}
        isInert={isInert}
        onClick={handleClick}
      >
        {buttonContent}
      </ButtonLink>
    )
  }

  return (
    <button
      {...restProps}
      type={type}
      className={buttonClassName}
      ref={ref as React.RefObject<HTMLButtonElement | null>}
      onClick={handleClick}
      disabled={isInert}
      tabIndex={tabIndex}
    >
      {buttonContent}
    </button>
  )
}

ButtonInner.displayName = 'ButtonInner'
